import {
  HeadContent,
  Outlet,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { createPortal } from "react-dom";
import { DefaultCatchBoundary } from "~/components/default-catch-boundary";
import { NotFound } from "~/components/not-found";
import { RouterDocumentTitleSync } from "~/lib/router-document-title";
import type { AppRouterContext } from "~/lib/router-runtime";
import { createSocialHeadMeta } from "~/lib/social-head";
import { DEFAULT_SOCIAL_METADATA } from "~/lib/social-metadata";
import { THEME_CHROME_COLORS } from "~/lib/theme-chrome";

const ROOT_HEAD_CONTENT = <HeadContent />;

export const Route = createRootRouteWithContext<AppRouterContext>()({
  errorComponent: DefaultCatchBoundary,
  head: () => ({
    links: [
      {
        href: "/favicon-48x48.png",
        rel: "alternate icon",
        sizes: "48x48",
        type: "image/png",
      },
      {
        href: "/favicon.svg",
        rel: "icon",
        type: "image/svg+xml",
      },
      {
        href: "/apple-touch-icon.png",
        rel: "apple-touch-icon",
        sizes: "180x180",
      },
      {
        href: "/site.webmanifest",
        rel: "manifest",
      },
      {
        color: THEME_CHROME_COLORS.light,
        href: "/safari-pinned-tab.svg",
        rel: "mask-icon",
      },
    ],
    meta: [
      ...createSocialHeadMeta(DEFAULT_SOCIAL_METADATA),
      {
        content: "Glider.fi",
        name: "apple-mobile-web-app-title",
      },
      {
        content: THEME_CHROME_COLORS.light,
        name: "msapplication-TileColor",
      },
      {
        content: THEME_CHROME_COLORS.light,
        name: "theme-color",
      },
    ],
  }),
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

function RootComponent() {
  const shouldShowRouterDevtools =
    import.meta.env.DEV &&
    import.meta.env.VITE_DISABLE_ROUTER_DEVTOOLS !== "1" &&
    import.meta.env.VITE_ENABLE_ROUTER_DEVTOOLS === "1";

  return (
    <>
      {typeof document === "undefined"
        ? null
        : import.meta.env.MODE === "test"
          ? ROOT_HEAD_CONTENT
          : createPortal(ROOT_HEAD_CONTENT, document.head)}
      <RouterDocumentTitleSync />
      <Outlet />
      {shouldShowRouterDevtools ? (
        <TanStackRouterDevtools position="bottom-right" />
      ) : null}
    </>
  );
}
